import type { ComponentProps } from 'react';
import React, { Children, cloneElement, isValidElement, useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import type { InputSize } from '~/components/Input/types';
import { TableCellHeader } from '../../tailwindComponents/Table';
import styles from './Table.module.scss';

type TableProps = Omit<ComponentProps<'div'>, 'size'> & {
  gridTemplateColumns: string;
  bordered?: boolean;
  size?: InputSize;
};

export const Table = ({ bordered, gridTemplateColumns, children, className, size }: TableProps) => {
  const [hoverId, setHoverId] = useState<React.Key | null>();

  return (
    <div
      className={cn(
        'grid grow-1 relative',
        styles.table,
        bordered ? styles.bordered : undefined,
        size ? styles[size] : undefined,
        className,
      )}
      style={{ gridTemplateColumns }}>
      {Children.map(children, (child) => {
        if (isValidElement<CellProps>(child)) {
          if (child.type === TableCellHeader) {
            return cloneElement(child, {
              onMouseEnter: () => setHoverId?.(child.key),
              onMouseLeave: () => setHoverId?.(null),
            });
          }
          return cloneElement(child, {
            hover: hoverId === child.key,
            onMouseEnter: () => setHoverId?.(child.key),
            onMouseLeave: () => setHoverId?.(null),
          });
        } else {
          return child;
        }
      })}
    </div>
  );
};

type Table2Props = ComponentProps<'div'> & {
  gridTemplateColumns: string;
};
export const Table2 = ({ gridTemplateColumns, children, className }: Table2Props) => {
  return (
    <div
      className={cn(
        'grid grow-1 relative overflow-auto',
        'border border-gray-200 dark:border-gray-800',
        'bg-white dark:bg-gray-900 rounded-lg shadow-sm',
        className,
      )}
      style={{ gridTemplateColumns }}>
      {children}
    </div>
  );
};

type CellProps = ComponentProps<'div'> & {
  index: number;
  active?: boolean;
  hover?: boolean;
  test?: boolean;
};
export const Cell = ({ index, active, children, hover, onClick, className, ...props }: CellProps) => {
  return (
    <div
      className={cn(
        'flex flex-row items-center gap-1 text-sm font-normal',
        styles.cell,
        index === 0 ? styles.first : undefined,
        active ? styles.active : undefined,
        hover ? styles.hover : undefined,
        onClick != null ? styles.hoverable : undefined,
        className,
      )}
      onClick={onClick}
      {...props}>
      {children}
    </div>
  );
};

type Cell2Props = ComponentProps<'div'>;
export const Cell2 = ({ children, onClick, className, ...props }: Cell2Props) => {
  return (
    <div className={cn('flex flex-row items-center gap-1 text-sm font-normal', className)} onClick={onClick} {...props}>
      {children}
    </div>
  );
};

type RowProps = ComponentProps<'div'> & {
  active?: boolean;
};
export const Row = ({ children, onClick, className, active, ...props }: RowProps) => {
  return (
    <div
      className={cn(
        'relative col-span-full grid grid-cols-subgrid group border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden',
        'first:rounded-t-lg last:rounded-b-lg first:border-t-0',
        {
          'cursor-pointer hover:bg-blue-100/5 hover:text-blue-700': onClick,
          'bg-blue-100/5 text-blue-700 dark:bg-blue-900/5 dark:text-blue-300': active,
        },
        className,
      )}
      onClick={onClick}
      {...props}>
      {children}
    </div>
  );
};

type CellLoadingProps = ComponentProps<'div'> & {
  index: number;
};
export const CellLoading = ({ children, index }: CellLoadingProps) => {
  return (
    <div
      className={cn(
        styles.cell,
        styles.cellLoading,
        'flex flex-row items-baseline justify-center gap-1 text-sm font-normal',
        '[grid-column-start:1] [grid-column-end:-1]',
        index === 0 ? styles.first : undefined,
      )}>
      <div className={cn('size-4 m-0 &>svg:size-full &>svg:block', styles.icon)}>
        <WaveSpinner />
      </div>
      {children}
    </div>
  );
};
